import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Baby, BookOpen, CalendarDays, Heart, Home, Loader2, Palmtree, Plus, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { AppHeader } from "@/components/AppHeader";
import { useFamily } from "@/lib/useFamily";
import { formatDate } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MILESTONE_CATEGORIES = {
    birth: { label: "Birth", icon: Baby },
    wedding: { label: "Wedding", icon: Heart },
    anniversary: { label: "Anniversary", icon: CalendarDays },
    first_home: { label: "First Home", icon: Home },
    holiday: { label: "Family Holiday", icon: Palmtree },
    heritage: { label: "Heritage Story", icon: BookOpen },
    other: { label: "Milestone", icon: Sparkles },
};

const INPUT_CLASS =
    "w-full rounded-2xl border-2 border-warmgray bg-[#FDFBF9] px-5 py-3 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach";

export default function Milestones() {
    const { checking, user, family } = useFamily();
    const [milestones, setMilestones] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("birth");
    const [date, setDate] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const { data } = await axios.get(`${API}/milestones`, { withCredentials: true });
        setMilestones(data.milestones);
    }, []);

    useEffect(() => {
        if (!checking) load();
    }, [checking, load]);

    const create = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setSaving(true);
        try {
            await axios.post(
                `${API}/milestones`,
                { title, category, description, milestone_date: date },
                { withCredentials: true }
            );
            setModalOpen(false);
            setTitle("");
            setDescription("");
            setDate("");
            setCategory("birth");
            await load();
            toast.success("Milestone created. Start adding memories to it.");
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not create milestone.");
        }
        setSaving(false);
    };

    if (checking) {
        return (
            <main data-testid="milestones-loading" className="flex min-h-screen items-center justify-center bg-cream">
                <Loader2 size={32} className="animate-spin text-lavender" />
            </main>
        );
    }

    return (
        <main data-testid="milestones-page" className="min-h-screen bg-cream text-navy">
            <AppHeader user={user} />
            <div className="mx-auto max-w-6xl px-6 py-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                    className="flex flex-wrap items-end justify-between gap-4"
                >
                    <div>
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C98A94]">
                            {family.name} · Chapters of your story
                        </p>
                        <h1 data-testid="milestones-heading" className="mt-2 font-display text-3xl tracking-tight md:text-4xl">
                            Family Milestones
                        </h1>
                        <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#2C3347]">
                            Births, weddings, first homes — each milestone is a dedicated space where your family adds voice stories, photos, and written memories together.
                        </p>
                    </div>
                    <button
                        data-testid="add-milestone-button"
                        onClick={() => setModalOpen(true)}
                        className="btn-gradient flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                    >
                        <Plus size={18} /> New Milestone
                    </button>
                </motion.div>

                {milestones.length === 0 ? (
                    <div data-testid="milestones-empty" className="mt-14 rounded-3xl border-2 border-dashed border-warmgray bg-ivory p-14 text-center">
                        <Sparkles size={32} className="mx-auto text-lavender" />
                        <h2 className="mt-4 font-display text-2xl">Every family has its chapters.</h2>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-navy">
                            Create your first milestone — a birth, a wedding, a heritage story — and let everyone fill it with memories.
                        </p>
                        <button
                            data-testid="add-milestone-button-empty"
                            onClick={() => setModalOpen(true)}
                            className="btn-gradient mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            <Plus size={16} /> Create your first milestone
                        </button>
                    </div>
                ) : (
                    <div data-testid="milestones-grid" className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {milestones.map((m) => {
                            const cat = MILESTONE_CATEGORIES[m.category] || MILESTONE_CATEGORIES.other;
                            return (
                                <motion.div
                                    key={m.milestone_id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                                >
                                    <Link
                                        to={`/milestones/${m.milestone_id}`}
                                        data-testid={`milestone-card-${m.milestone_id}`}
                                        className="block overflow-hidden rounded-3xl border-2 border-warmgray bg-ivory transition-transform duration-300 hover:-translate-y-1"
                                    >
                                        {m.cover_path ? (
                                            <img
                                                src={`${API}/files/${m.cover_path}`}
                                                alt={m.title}
                                                loading="lazy"
                                                className="h-40 w-full object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-40 w-full items-center justify-center bg-gradient-to-br from-lavender to-peach">
                                                <cat.icon size={40} className="text-white" />
                                            </div>
                                        )}
                                        <div className="p-5">
                                            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                                                {cat.label}
                                                {m.milestone_date ? ` · ${formatDate(m.milestone_date)}` : ""}
                                            </p>
                                            <h2 className="mt-1.5 font-display text-xl leading-snug">{m.title}</h2>
                                            {m.description && <p className="mt-1 line-clamp-2 text-sm text-[#2C3347]">{m.description}</p>}
                                            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#2C3347]">
                                                {m.memory_count} {m.memory_count === 1 ? "memory" : "memories"}
                                            </p>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>

            {modalOpen && (
                <div data-testid="add-milestone-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4 backdrop-blur-sm" onClick={() => !saving && setModalOpen(false)}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl border-2 border-warmgray bg-ivory p-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-2xl">New Milestone</h2>
                            <button data-testid="close-milestone-modal" onClick={() => !saving && setModalOpen(false)} aria-label="Close" className="text-navy/60 hover:text-navy">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={create} className="mt-5 space-y-3">
                            <input
                                data-testid="milestone-title-input"
                                type="text"
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Title — e.g. Grandma Anna's Wedding"
                                aria-label="Milestone title"
                                className={INPUT_CLASS}
                            />
                            <div>
                                <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-navy/60">Category</p>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(MILESTONE_CATEGORIES).map(([id, c]) => (
                                        <button
                                            key={id}
                                            type="button"
                                            data-testid={`milestone-category-${id}`}
                                            onClick={() => setCategory(id)}
                                            className={`flex items-center gap-1.5 rounded-full border-2 px-3.5 py-2 text-xs font-semibold transition-colors duration-300 ${
                                                category === id ? "border-navy bg-navy text-white" : "border-warmgray text-navy hover:border-peach"
                                            }`}
                                        >
                                            <c.icon size={13} /> {c.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label htmlFor="milestone-date" className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-navy/60">
                                    Date (optional)
                                </label>
                                <input
                                    id="milestone-date"
                                    data-testid="milestone-date-input"
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className={INPUT_CLASS}
                                />
                            </div>
                            <textarea
                                data-testid="milestone-description-input"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="What makes this chapter special? (optional)"
                                aria-label="Description"
                                rows={3}
                                className={`${INPUT_CLASS} resize-none leading-relaxed`}
                            />
                            <button
                                data-testid="milestone-create-button"
                                type="submit"
                                disabled={saving || !title.trim()}
                                className="btn-gradient flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                            >
                                {saving && <Loader2 size={15} className="animate-spin" />}
                                {saving ? "Creating…" : "Create Milestone"}
                            </button>
                        </form>
                    </motion.div>
                </div>
            )}
        </main>
    );
}
